import React from 'react';
import domainModel from 'domain-model';

import { Row, Col, ButtonGroup, Button, Badge } from 'reactstrap';
import { connect } from 'react-redux';
import _ from 'lodash';

import type { ProcurementArticle, CartRow } from 'domain-model/types/pcs';
import type {
  PricedArticleReference,
  StockKeepingUnit,
} from 'domain-model/types/oms';
import type { ShopConfig } from 'domain-model/types/supplier-gateway';
import Money from '../Money';
import DailyPriceIcon from '../article/DailyPriceIcon';
import PriceSpan from '../PriceSpan';
import ArticleQuantityInput from '../inputs/ArticleQuantityInput';
import { recalculateIncreaseInput } from '../inputs/articleQuantityInputCalculators';

import type { PersistedMultiCartState } from '../../reducers/persisted-multi-cart.reducer';

import {
  updateQuantityOnCartRow as _updateQuantityOnCartRow,
  type Authentication,
} from '../../actions/persisted-multi-cart.actions';

function startCaseUTF8(str: string) {
  return str
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letter: string) => letter.toUpperCase());
}

const getTotalCost = (
  articleViewModel: any,
  quantity: number,
  orderedSKU: StockKeepingUnit | undefined,
) => {
  if (!orderedSKU) {
    return articleViewModel.salesPrice * quantity;
  }
  return (
    orderedSKU.quantityOfMainUnitPerUnit! *
    articleViewModel.salesPrice *
    quantity
  );
};

const getStockKeepingUnits = (
  articleViewModel: any,
  procurementArticle?: ProcurementArticle,
): StockKeepingUnit[] => {
  const skus = _(articleViewModel.stockKeepingUnits || [])
    .sortBy('quantityOfMainUnitPerUnit')
    .value();

  if (procurementArticle) {
    return skus.filter(
      (sku: StockKeepingUnit) =>
        // @ts-expect-error we need to type reference types
        sku.name === 'kolli' || sku.name === procurementArticle.SKUName,
    );
  }
  return skus;
};

const getCartKey = (article: PricedArticleReference) => {
  if (!article.myOrderFullfillmentCenter) {
    return null;
  }
  return domainModel.cart.getCartKey({
    orderFullfillmentCenter: article.myOrderFullfillmentCenter,
    currency: article.currency,
  });
};

const getMyCartRow = (
  article: PricedArticleReference,
  procurementArticle: ProcurementArticle | undefined,
  persistedMultiCart: PersistedMultiCartState,
  explicitCartKey?: string,
): CartRow | undefined => {
  if (!article.myOrderFullfillmentCenter) {
    return undefined;
  }
  const cartKey = explicitCartKey ?? getCartKey(article);
  if (!cartKey) {
    return undefined;
  }
  const cart = persistedMultiCart.carts[cartKey];
  if (!cart || !cart.cartRows) {
    return undefined;
  }
  return cart.cartRows.find(item => {
    if (procurementArticle) {
      return item.procurementArticle?.id === procurementArticle.id;
    }
    return (
      // @ts-expect-error we need to type reference types
      item.pricedArticle.id === article.id && !item.procurementArticle
    );
  });
};

const SKUInput = ({
  skus,
  myOrderedSKU,
  sendSetSkuOnCartRow,
  mainSKU,
}: {
  skus: StockKeepingUnit[];
  myOrderedSKU: StockKeepingUnit | undefined;
  sendSetSkuOnCartRow: (name: string, orderedSKU: StockKeepingUnit) => void;
  mainSKU: StockKeepingUnit | undefined;
}) => {
  const getButtonValue = (mySku: StockKeepingUnit) => {
    const nameWithCasing = startCaseUTF8(mySku.name || '');
    if (mySku.isMainUnit) {
      return nameWithCasing;
    }
    const mainSkuPlural = domainModel.getPluralization(
      'article.stockKeepingUnits.names',
      mainSKU?.name,
    );

    if (mySku.quantityOfMainUnitPerUnit! >= 1) {
      return `${nameWithCasing} (${mySku.quantityOfMainUnitPerUnit} ${mainSkuPlural})`;
    }

    return `${nameWithCasing} (${_.round(
      1 / mySku.quantityOfMainUnitPerUnit!,
      2,
    )} ${mySku.name}/${mainSkuPlural})`;
  };

  return (
    <Row className="align-items-center g-0">
      <ButtonGroup style={{ height: '25px' }}>
        {skus.map((sku: StockKeepingUnit) => (
          <Button
            size="sm"
            color="secondary"
            key={sku.id}
            className="article-name p-0"
            shadow-none="true" // Changed to string
            outline
            active={myOrderedSKU?.id === sku.id}
            onClick={() => sendSetSkuOnCartRow('orderedSKU', sku)}
          >
            {getButtonValue(sku)}
          </Button>
        ))}
      </ButtonGroup>
    </Row>
  );
};

const PriceComparison = ({ articleViewModel }: { articleViewModel: any }) => {
  if (!articleViewModel.displayPriceComparison) {
    return <> </>;
  }
  return (
    <div>
      <Money
        value={articleViewModel.displayPriceComparison}
        currency={articleViewModel.currency}
      />
      /{articleViewModel.displayPriceComparisonUnit}
    </div>
  );
};

const PricePerUnit = ({
  articleViewModel,
  myOrderedSKU,
}: {
  articleViewModel: any;
  myOrderedSKU: StockKeepingUnit | undefined;
}) => {
  if (!myOrderedSKU) {
    return (
      <>
        <PriceSpan pricedArticle={articleViewModel} />
        <DailyPriceIcon article={articleViewModel} />
      </>
    );
  }

  return (
    <>
      <Money
        value={
          (myOrderedSKU.quantityOfMainUnitPerUnit || 0) *
          articleViewModel.salesPrice
        }
        currency={articleViewModel.currency}
      ></Money>
      {` /${myOrderedSKU.name}`}
      <DailyPriceIcon article={articleViewModel} />
    </>
  );
};

const getMyOrderedSKU = (
  myCartRow: CartRow | undefined,
  skus: StockKeepingUnit[] | undefined,
  mainSKU: StockKeepingUnit | undefined,
) => {
  if (myCartRow?.orderedSKU) {
    return myCartRow.orderedSKU;
  }
  if (skus?.find(sku => sku.name === mainSKU?.name)) {
    return mainSKU;
  }
  if (skus) {
    return skus[0];
  }
  return mainSKU;
};

function CartArticleQuantityInputNew({
  explicitCartKey,
  article,
  shadow,
  procurementArticle,
  persistedMultiCart,
  updateQuantityOnCartRow,
  shopConfig,
  disabled,
  dontShowSKUSwitch,
}: {
  explicitCartKey?: string;
  article: PricedArticleReference;
  shadow?: boolean;
  procurementArticle?: ProcurementArticle;
  persistedMultiCart: PersistedMultiCartState;
  updateQuantityOnCartRow: Function;
  shopConfig: ShopConfig;
  disabled?: boolean;
  dontShowSKUSwitch?: boolean;
}): JSX.Element {
  const articleViewModel = domainModel.pricedArticle.getViewModel(article);

  const warehouseStatus =
    domainModel.pricedArticle.warehouseStatus(articleViewModel);

  const skus = getStockKeepingUnits(articleViewModel, procurementArticle);
  const mainSKU =
    articleViewModel.stockKeepingUnits?.find(
      (sku: StockKeepingUnit) => sku.isMainUnit,
    ) || skus[0];

  const numberOfSKU = skus?.length || 0;

  const myCartRow = getMyCartRow(
    article,
    procurementArticle,
    persistedMultiCart,
    explicitCartKey,
  );

  const myOrderedSKU = getMyOrderedSKU(myCartRow, skus, mainSKU);

  const disableInput = !getCartKey(article) || disabled;

  const outOfSync = myCartRow?.outOfSync || false;

  const quantity = myCartRow?.quantity || 0;

  const totalCost = getTotalCost(articleViewModel, quantity, myOrderedSKU);

  const sendUpdateQuantityOnCartRow = (name: string, value: number) => {
    updateQuantityOnCartRow(
      articleViewModel,
      value,
      procurementArticle,
      myOrderedSKU,
    );
  };

  const sendSetSkuOnCartRow = (
    name: string,
    orderedSKU: StockKeepingUnit | undefined,
  ) => {
    const newQuantity =
      recalculateIncreaseInput(quantity, orderedSKU) ||
      orderedSKU?.minOrderQuantity ||
      orderedSKU?.stepOrderIncrement ||
      1;
    updateQuantityOnCartRow(
      articleViewModel,
      newQuantity,
      procurementArticle,
      orderedSKU,
    );
  };

  const orderUnitSize = domainModel.article.getDisplaySKUSize(
    articleViewModel,
    myOrderedSKU as StockKeepingUnit,
  );
  const articleHasPrice =
    articleViewModel.salesPrice !== null ||
    articleViewModel.salesPrice === undefined;

  return (
    <Row
      className={`align-items-center g-0 w-100
        ${shadow ? 'shadow' : ''}`}
    >
      <Col>
        {!dontShowSKUSwitch && quantity > 0 && numberOfSKU > 1 && (
          <SKUInput
            skus={skus}
            myOrderedSKU={myOrderedSKU}
            sendSetSkuOnCartRow={sendSetSkuOnCartRow}
            mainSKU={mainSKU}
          />
        )}
        <Row className="align-items-center g-0 bg-light text-sm d-flex">
          <Col
            className="displayOrderUnitSize-article d-flex justify-content-center text-center text-nowrap align-items-center"
            style={{ flex: 1 }}
          >
            {orderUnitSize}
          </Col>
          <Col
            className={`displayPriceComparison-article d-flex justify-content-center text-center text-nowrap align-items-center ${
              articleViewModel.shouldHighlightPriceAsDiscounted
                ? 'text-danger'
                : ''
            }`}
            style={{ flex: 1 }}
          >
            <PriceComparison articleViewModel={articleViewModel} />
          </Col>
          <Col
            className="price-article d-flex justify-content-center text-center text-nowrap align-items-center"
            style={{ flex: 1 }}
          >
            <PricePerUnit
              articleViewModel={articleViewModel}
              myOrderedSKU={myOrderedSKU}
            />
          </Col>
          <Col
            className="additional-info d-flex justify-content-center text-center text-nowrap align-items-center ms-auto"
            style={{ flex: 0 }}
          >
            <Row className="align-items-center mx-1">
              {articleViewModel.priceType && (
                <Badge color="info">{articleViewModel.getPriceType}</Badge>
              )}
              {outOfSync && <Badge color="warning">OutOfSync</Badge>}
            </Row>
          </Col>
        </Row>
        {!articleHasPrice && (
          <Col className="text-center px-1 articleQuantityGrid missing-price text-sm">
            <strong>Pris saknas.</strong>{' '}
            <span className="text-xs">
              Kontakta {articleViewModel.myOrderFullfillmentCenter?.name} om du
              vill beställa denna vara
            </span>
          </Col>
        )}
        {articleHasPrice && (
          <>
            {!warehouseStatus.isPossibleToOrder && (
              <Col className="text-center mb-2 mt-1" sm={12}>
                {warehouseStatus.text || 'Tillfälligt slut'}
              </Col>
            )}
            {quantity > 0 && warehouseStatus.isPossibleToOrder && (
              <Col className="text-center just-buttons" xs={12} sm={12}>
                <Row className="justify-content-end align-items-center g-0">
                  <Col xs={8}>
                    <ArticleQuantityInput
                      article={articleViewModel}
                      orderedSKU={myOrderedSKU}
                      value={quantity}
                      onChange={sendUpdateQuantityOnCartRow}
                      cartRow={myCartRow}
                      disabled={disableInput}
                      shopConfig={shopConfig}
                      wideClickable={true}
                      isGothiaMode={true}
                    />
                  </Col>
                  <Col xs={4}>
                    <strong>
                      <Money
                        value={totalCost}
                        currency={articleViewModel.currency}
                      />
                    </strong>
                  </Col>
                </Row>
              </Col>
            )}
            {!disabled &&
              quantity === 0 &&
              warehouseStatus.isPossibleToOrder && (
                <Row>
                  <Col xs={12}>
                    <Button
                      color="primary"
                      outline
                      size="sm"
                      block
                      onClick={() =>
                        sendSetSkuOnCartRow('never-used', myOrderedSKU)
                      }
                    >
                      Lägg i varukorg
                    </Button>
                  </Col>
                </Row>
              )}
          </>
        )}
      </Col>
    </Row>
  );
}

const mapStateToProps = ({
  persistedMultiCart,
  authentication,
  shopConfig,
}: {
  persistedMultiCart: PersistedMultiCartState;
  authentication: Authentication; // Replace 'any' with the actual type of 'authentication'
  shopConfig: ShopConfig; // Replace 'any' with the actual type of 'shopConfig'
}) => ({
  persistedMultiCart,
  sessionUser: authentication.sessionUser,
  shopConfig,
});

const mapDispatchToProps = {
  updateQuantityOnCartRow: _updateQuantityOnCartRow,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CartArticleQuantityInputNew);
